import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import DramaCard from '@/components/feature/DramaCard';
import { dramas } from '@/mocks/dramas';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(initial);
  const query = params.get('q') ?? '';

  useEffect(() => {
    document.title = '搜索剧集 - 善良的小姨子在线播放';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', '在善良的小姨子在线播放站内搜索影视内容，支持按剧名、题材与关键词快速查找相关剧集。');
    }
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return dramas.filter((d) =>
      [d.title, d.subtitle, d.synopsis, d.category, ...d.tags]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [query]);

  const recommended = dramas.slice(0, 5);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = keyword.trim();
    setParams(next ? { q: next } : {});
  };

  return (
    <Layout>
      <div className="pt-16 md:pt-[68px]">
        <section className="w-full bg-background-100 px-4 md:px-16 lg:px-24 pt-8 md:pt-12 pb-8 md:pb-12">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">搜索</span>
          </nav>
          <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl">搜索剧集</h1>
          <p className="text-foreground-600 text-sm md:text-base mt-3">
            输入剧名、题材或关键词，即可快速查找“善良的小姨子”相关影视内容。
          </p>
          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
              <input
                type="text"
                name="keyword"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="例如：善良的小姨子、家庭情感、治愈……"
                className="w-full rounded-md border border-background-300/70 bg-background-50 pl-11 pr-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 py-10 md:py-14">
          {query ? (
            results.length > 0 ? (
              <>
                <p className="text-sm text-foreground-600 mb-6">
                  搜索“<span className="text-primary-700 font-medium">{query}</span>”，共找到{' '}
                  <span className="text-primary-700 font-semibold">{results.length}</span> 个结果
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                  {results.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} />
                  ))}
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-background-200/70 bg-background-50 py-16 flex flex-col items-center text-center px-4">
                <i className="ri-search-eye-line text-5xl text-foreground-300" />
                <p className="mt-4 text-foreground-700 font-medium">
                  未找到与“{query}”相关的剧集
                </p>
                <p className="text-sm text-foreground-500 mt-2">换个关键词试试，比如“家庭”“治愈”“都市”。</p>
              </div>
            )
          ) : (
            <div className="rounded-lg border border-dashed border-background-300/70 bg-background-50 py-16 flex flex-col items-center text-center px-4">
              <i className="ri-film-line text-5xl text-foreground-300" />
              <p className="mt-4 text-foreground-700 font-medium">开始搜索你感兴趣的剧集</p>
              <p className="text-sm text-foreground-500 mt-2">支持按剧名、题材与关键词模糊查找。</p>
            </div>
          )}

          {(!query || results.length === 0) && (
            <div className="mt-14">
              <div className="flex items-center gap-2 mb-6">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">为你推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
                {recommended.map((drama) => (
                  <DramaCard key={drama.id} drama={drama} />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}